import { Button, Card, Descriptions, message, Switch } from "antd";
import type React from "react";
import { useCallback, useEffect, useRef, useState } from "react";
import { type ChargeCodeApi, chargeCodeApi, chooseChargeCodeApi } from "@/config/charge-code-api";
import { useAuth } from "@/contexts/AuthContext";
import { requireAdditionalInfoOnNextLogin } from "@/contexts/auth/redirect";
import { refreshUserProfile } from "@/contexts/auth/services/staffService";
import { useTimezone } from "@/hooks/useTimezone";
import { analytics } from "@/lib/analytics";
import { envStorage } from "@/lib/storage/envIsolatedStorage";
import { useUser, useUserProfile, useUserStore } from "@/stores";
import { PADDING } from "../constants";
import {
  type CognitoUserResponse,
  testCognitoGetUser,
  testFullRefreshFlow,
} from "../utils/apiTestUtils";
import { CognitoUserModal } from "./CognitoUserModal";

const RELOAD_AFTER_SWITCH_MS = 1000;

interface DebugPanelContentProps {
  onClose: () => void;
}

export const DebugPanelContent: React.FC<DebugPanelContentProps> = ({ onClose }) => {
  const cardRef = useRef<HTMLDivElement>(null);
  const profile = useUserProfile();
  const user = useUser();
  const userStore = useUserStore();
  const { isAuthenticated, loading, logout, signinTpass } = useAuth();
  const timezone = useTimezone();
  const browserTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const [cognitoModalVisible, setCognitoModalVisible] = useState(false);
  const [cognitoUserData, setCognitoUserData] = useState<CognitoUserResponse | null>(null);
  const [cognitoLoading, setCognitoLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [mixpanelDistinctId, setMixpanelDistinctId] = useState<string | undefined>(undefined);
  const [mixpanelOptOut, setMixpanelOptOut] = useState<string>("Unknown");

  const mixpanelEnv = import.meta.env.VITE_ENV || "development";
  const mixpanelToken = import.meta.env.VITE_MIXPANEL_TOKEN;
  const mixpanelEnabled = !!mixpanelToken && import.meta.env.VITE_MIXPANEL_ENABLED !== "false";
  const doNotTrack =
    navigator.doNotTrack === "1" ||
    (window as Window & { doNotTrack?: string }).doNotTrack === "1" ||
    (navigator as Navigator & { msDoNotTrack?: string }).msDoNotTrack === "1";

  const [permissionSystemEnabled, setPermissionSystemEnabled] = useState(() => {
    const stored = envStorage.getItem("debug_permission_system_enabled");
    return stored === null ? true : stored === "true";
  });

  const [sessionRefreshEnabled, setSessionRefreshEnabled] = useState(() => {
    const stored = envStorage.getItem("debug_session_refresh_enabled");
    return stored === "true"; // Default to false (only true if explicitly set)
  });

  const [consultationPatientInfoStickyEnabled, setConsultationPatientInfoStickyEnabled] = useState(
    () => {
      const stored = envStorage.getItem("debug_consultation_patient_info_sticky");
      return stored === null ? true : stored === "true";
    },
  );

  const [spokenChargeCodeApi, setSpokenChargeCodeApi] = useState<ChargeCodeApi>(chargeCodeApi);

  // Handle outside click to close
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (cardRef.current && !cardRef.current.contains(event.target as Node)) {
        onClose();
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [onClose]);

  const refreshMixpanelStatus = useCallback(() => {
    setMixpanelDistinctId(analytics.getUserId());
    if (!mixpanelEnabled) {
      setMixpanelOptOut("Disabled");
      return;
    }
    setMixpanelOptOut(analytics.hasOptedOut() ? "Yes" : "No");
  }, [mixpanelEnabled]);

  useEffect(() => {
    refreshMixpanelStatus();
  }, [refreshMixpanelStatus]);

  const handleTestCognitoGetUser = async () => {
    setCognitoLoading(true);
    setCognitoModalVisible(true);

    try {
      const data = await testCognitoGetUser();
      setCognitoUserData(data);
    } finally {
      setCognitoLoading(false);
    }
  };

  const handleRefreshUserProfile = async () => {
    setRefreshing(true);
    try {
      const updatedProfile = await refreshUserProfile();
      if (updatedProfile) {
        userStore.updateProfile(updatedProfile);
        userStore.setPermissions(updatedProfile.permissions || []);
        message.success("User profile refreshed successfully");
      } else {
        message.error("Failed to refresh user profile");
      }
    } catch (error) {
      console.error("Error refreshing user profile:", error);
      message.error("Error refreshing user profile");
    } finally {
      setRefreshing(false);
    }
  };

  const handlePermissionSystemToggle = (checked: boolean) => {
    setPermissionSystemEnabled(checked);
    envStorage.setItem("debug_permission_system_enabled", String(checked));
    message.success(`Permission system ${checked ? "enabled" : "disabled"}. Reloading page...`);
    setTimeout(() => {
      window.location.reload();
    }, 1000);
  };

  const handleSessionRefreshToggle = (checked: boolean) => {
    setSessionRefreshEnabled(checked);
    envStorage.setItem("debug_session_refresh_enabled", String(checked));

    // Dispatch custom event to notify the hook
    window.dispatchEvent(new Event("session-refresh-toggle"));

    message.success(
      `Session auto-refresh ${checked ? "enabled" : "disabled"}. ${checked ? "Will refresh every 13 minutes." : ""}`,
    );
  };

  const handleConsultationPatientInfoStickyToggle = (checked: boolean) => {
    setConsultationPatientInfoStickyEnabled(checked);
    envStorage.setItem("debug_consultation_patient_info_sticky", String(checked));

    window.dispatchEvent(new Event("consultation-sticky-toggle"));

    message.success(`Consultation patient info sticky ${checked ? "enabled" : "disabled"}.`);
  };

  const handleChargeCodeApiToggle = (checked: boolean) => {
    const chosen: ChargeCodeApi = checked ? "v2" : "v1";
    chooseChargeCodeApi(chosen);
    setSpokenChargeCodeApi(chosen);
    message.success(`Charge-code API switched to ${chosen}. Reloading page...`);
    setTimeout(() => {
      window.location.reload();
    }, RELOAD_AFTER_SWITCH_MS);
  };

  const handleTest401 = async () => {
    message.info("Simulating 401 error...");

    // Clear ALL tokens to simulate complete session expiration
    // Note: tokens now have environment prefixes
    envStorage.removeItem("accessToken");
    envStorage.removeItem("refreshToken");
    envStorage.removeItem("sessionId");

    // Directly trigger the 401 auto-login flow
    // Import the interceptor module to access triggerTpassLogin
    const { resetAllStores } = await import("@/stores/utils/resetAllStores");

    // Reset stores like the 401 handler does while preserving same-user AdditionalInfo.
    resetAllStores({ preserveAdditionalInfo: true });

    requireAdditionalInfoOnNextLogin(window.location.pathname, { force: true });
    await signinTpass();
  };

  return (
    <>
      <Card
        ref={cardRef}
        title="Debug Panel"
        style={{
          position: "fixed",
          bottom: `${PADDING}px`,
          right: `${PADDING}px`,
          width: "360px",
          maxHeight: "80vh",
          zIndex: 1000,
          display: "flex",
          flexDirection: "column",
        }}
        className="w-[360px]"
        extra={
          <Button size="small" onClick={onClose}>
            Close
          </Button>
        }
        styles={{
          body: {
            padding: 0,
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
          },
        }}
      >
        {/* Scrollable content area */}
        <div
          style={{
            overflowY: "auto",
            overflowX: "hidden",
            flex: 1,
            padding: "24px",
          }}
        >
          <Descriptions column={1} bordered size="small">
            <Descriptions.Item label="Authentication">
              {isAuthenticated ? "Authenticated" : "Unauthenticated"}
            </Descriptions.Item>
            <Descriptions.Item label="Loading">
              {loading ? "Loading" : "Completed"}
            </Descriptions.Item>
            <Descriptions.Item label="Charge-code API">
              <span className="font-mono text-blue-600">{chargeCodeApi()}</span>
            </Descriptions.Item>
            <Descriptions.Item label="Current Timezone">
              <span className="font-mono text-blue-600">{timezone}</span>
            </Descriptions.Item>
            <Descriptions.Item label="Browser Timezone">
              <span className="font-mono text-gray-600">{browserTimezone}</span>
            </Descriptions.Item>
            <Descriptions.Item label="ID">{profile?.id || "None"}</Descriptions.Item>
            <Descriptions.Item label="User ID">{profile?.userId || "None"}</Descriptions.Item>
            <Descriptions.Item label="User Name">{profile?.username || "None"}</Descriptions.Item>
            <Descriptions.Item label="Name">{profile?.name || "None"}</Descriptions.Item>
            <Descriptions.Item label="Full Name">{profile?.fullName || "None"}</Descriptions.Item>
            <Descriptions.Item label="Email">{profile?.email || "None"}</Descriptions.Item>
            <Descriptions.Item label="Locations">
              {profile?.locations?.length || 0}
            </Descriptions.Item>
            <Descriptions.Item label="Permissions">
              {profile?.permissions?.length || user.permissions?.length || 0}
            </Descriptions.Item>
            <Descriptions.Item label="Status">{profile?.status || "None"}</Descriptions.Item>
          </Descriptions>

          <Descriptions column={1} bordered size="small" className="mt-4" title="Analytics">
            <Descriptions.Item label="Env">{mixpanelEnv}</Descriptions.Item>
            <Descriptions.Item label="Mixpanel Enabled">
              {mixpanelEnabled ? "Yes" : "No"}
            </Descriptions.Item>
            <Descriptions.Item label="Token">
              {mixpanelToken ? "Configured" : "Missing"}
            </Descriptions.Item>
            <Descriptions.Item label="Distinct ID">
              {mixpanelDistinctId || "Unknown"}
            </Descriptions.Item>
            <Descriptions.Item label="Opted Out">{mixpanelOptOut}</Descriptions.Item>
            <Descriptions.Item label="DNT">{doNotTrack ? "On" : "Off"}</Descriptions.Item>
            <Descriptions.Item label="Path">{window.location.pathname}</Descriptions.Item>
            <Descriptions.Item label="User Agent">{navigator.userAgent}</Descriptions.Item>
            <Descriptions.Item label="Refresh">
              <Button size="small" onClick={refreshMixpanelStatus}>
                Refresh
              </Button>
            </Descriptions.Item>
          </Descriptions>

          <div className="mt-4 mb-4 flex items-center justify-between p-3 border border-solid border-border rounded">
            <span className="font-semibold">Permission System</span>
            <Switch
              checked={permissionSystemEnabled}
              onChange={handlePermissionSystemToggle}
              checkedChildren="ON"
              unCheckedChildren="OFF"
            />
          </div>

          <div className="mb-4 flex items-center justify-between p-3 border border-solid border-gray-300 rounded">
            <span className="font-semibold">Session Auto-Refresh (13min)</span>
            <Switch
              checked={sessionRefreshEnabled}
              onChange={handleSessionRefreshToggle}
              checkedChildren="ON"
              unCheckedChildren="OFF"
            />
          </div>

          <div className="mb-4 flex items-center justify-between p-3 border border-solid border-border rounded">
            <span className="font-semibold">Consultation Patient Card Sticky</span>
            <Switch
              checked={consultationPatientInfoStickyEnabled}
              onChange={handleConsultationPatientInfoStickyToggle}
              checkedChildren="ON"
              unCheckedChildren="OFF"
            />
          </div>

          <div className="mb-4 flex items-center justify-between p-3 border border-solid border-border rounded">
            <span className="font-semibold">Charge-code API</span>
            <Switch
              checked={spokenChargeCodeApi === "v2"}
              onChange={handleChargeCodeApiToggle}
              checkedChildren="V2"
              unCheckedChildren="V1"
            />
          </div>

          <div className="flex flex-col gap-2">
            <Button type="primary" onClick={handleRefreshUserProfile} loading={refreshing}>
              Refresh User Profile
            </Button>
            <Button type="primary" onClick={testFullRefreshFlow}>
              Test Token Refresh
            </Button>
            <Button type="primary" onClick={handleTestCognitoGetUser}>
              Test Cognito GetUser API
            </Button>
            <Button type="primary" danger onClick={handleTest401}>
              Test 401 Auto-Login
            </Button>
          </div>
        </div>

        {/* Fixed bottom actions */}
        <div
          style={{
            padding: "12px 24px",
            borderTop: "1px solid #f0f0f0",
            background: "#fff",
          }}
        >
          <div className="flex justify-between gap-2">
            <Button
              danger
              onClick={() => {
                logout();
                signinTpass();
                onClose();
              }}
            >
              Logout
            </Button>
            <Button
              onClick={() => {
                onClose();
                window.location.reload();
              }}
            >
              Refresh Page
            </Button>
          </div>
        </div>
      </Card>

      <CognitoUserModal
        visible={cognitoModalVisible}
        onClose={() => setCognitoModalVisible(false)}
        userData={cognitoUserData}
        loading={cognitoLoading}
      />
    </>
  );
};
